import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useWeb3 } from "../context/Web3Context";
import { useUserProfile } from "../hooks/useContract";
import { PACKAGES, DAILY_CLUB_NAMES } from "../config/constants";
import Button from "../components/Common/Button";

const SOCIALS = {
  telegram: "https://t.me/Triple8Global",
  whatsapp: "https://whatsapp.com/channel/0029VbDIyleIN9ixLHC2Lg19",
};

function Welcome() {
  const navigate = useNavigate();
  const { account, connect, isConnecting, isCorrectChain } = useWeb3();
  const { loading, isRegistered } = useUserProfile();

  useEffect(() => {
    if (account && isCorrectChain && !loading) {
      if (isRegistered) navigate("/home", { replace: true });
      else navigate("/register", { replace: true });
    }
  }, [account, isCorrectChain, loading, isRegistered, navigate]);

  return (
    <div className="landing">
      {/* HERO */}
      <section className="landing-hero">
        <img
          src="/logo.png"
          alt="888 Global"
          className="landing-logo"
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
        <h1 className="landing-title">888 GLOBAL</h1>
        <div className="landing-tagline">Decentralized Rewards Network</div>
        <p className="landing-sub">
          Structured on-chain compensation on BSC. Direct, matrix, and
          rank-based income — settled instantly in USDT.
        </p>

        <div style={{ width: "100%", maxWidth: 300, marginTop: 8 }}>
          <Button variant="primary" size="lg" full onClick={connect} loading={isConnecting}>
            {account ? "Loading..." : "Connect Wallet"}
          </Button>
        </div>
        <div className="landing-network">
          <span className="landing-network-dot" />
          Live on BSC · USDT
        </div>
      </section>

      {/* LIVE STATS BAR */}
      <div className="landing-stats-bar">
        <div className="landing-stat">
          <div className="landing-stat-value">8</div>
          <div className="landing-stat-label">Packages</div>
        </div>
        <div className="landing-stat-divider" />
        <div className="landing-stat">
          <div className="landing-stat-value">9</div>
          <div className="landing-stat-label">Ranks</div>
        </div>
        <div className="landing-stat-divider" />
        <div className="landing-stat">
          <div className="landing-stat-value">100%</div>
          <div className="landing-stat-label">On-Chain</div>
        </div>
      </div>

      {/* SOCIAL LINKS */}
      <div className="landing-socials">
        <a
          href={SOCIALS.telegram}
          target="_blank"
          rel="noopener noreferrer"
          className="landing-social telegram"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.13-.05-.18-.06-.06-.14-.04-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
          </svg>
          <span>Telegram</span>
        </a>
        <a
          href={SOCIALS.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          className="landing-social whatsapp"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
          </svg>
          <span>WhatsApp</span>
        </a>
      </div>

      {/* WHY */}
      <div className="section-header">
        <div className="section-title">Why 888 Global</div>
      </div>
      <div className="landing-features">
        <FeatureCard icon="⚡" title="Instant Payouts" desc="Every income stream settles to your wallet the moment it triggers." />
        <FeatureCard icon="🔗" title="Fully On-Chain" desc="Smart contract deployed on BSC. No hidden ledger." />
        <FeatureCard icon="🎯" title="Sequential Growth" desc="Activate 8 packages in order — from $8 to $444." />
        <FeatureCard icon="🛡" title="Ceiling Protected" desc="Automatic 3× / 4× ceiling per package. No infinite drain." />
      </div>

      {/* PACKAGES */}
      <div className="section-header">
        <div className="section-title">Package Tiers</div>
      </div>
      <div className="landing-pkgs">
        {PACKAGES.map((p) => (
          <div key={p.id} className="landing-pkg">
            <div className="landing-pkg-num">P{p.id}</div>
            <div className="landing-pkg-price">${p.price}</div>
          </div>
        ))}
      </div>

      {/* INCOME STREAMS */}
      <div className="section-header">
        <div className="section-title">3 Income Streams</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <IncomeRow label="Direct Referral" pct="45%" color="var(--cyan)" />
        <IncomeRow label="Matrix Levels (8 × 5%)" pct="40%" color="var(--violet)" />
        <IncomeRow label="Royalty Pools" pct="10%" color="var(--magenta)" />
      </div>

      {/* RANK LADDER — 7 royalty ranks */}
      <div className="section-header">
        <div className="section-title">Rank Ladder</div>
      </div>
      <div className="landing-ranks">
        {[1, 2, 3, 4, 5, 6, 7].map((id) => (
          <div key={id} className="landing-rank">
            <div className="landing-rank-num">{id}</div>
            <div className="landing-rank-name">{DAILY_CLUB_NAMES[id]}</div>
          </div>
        ))}
      </div>

      {/* HOW IT WORKS */}
      <div className="section-header">
        <div className="section-title">Get Started in 3 Steps</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <StepRow n="01" title="Connect Wallet" desc="Trust Wallet, MetaMask, or any Web3 wallet." />
        <StepRow n="02" title="Register & Activate" desc="One-click approve USDT + register with a referrer." />
        <StepRow n="03" title="Earn & Claim" desc="Rewards accrue on-chain. Claim anytime." />
      </div>

      {/* FINAL CTA */}
      <div id="connect-cta" className="landing-cta">
        <div className="landing-cta-title">Ready to Join?</div>
        <div className="landing-cta-sub">
          Connect your wallet — one transaction to become a member.
        </div>
        <Button variant="primary" size="lg" full onClick={connect} loading={isConnecting}>
          {account ? "Loading..." : "Connect Wallet"}
        </Button>
      </div>

      {/* Footer */}
      <div className="landing-footer">
        <div className="landing-footer-brand">888 GLOBAL</div>
        <div className="landing-footer-net">BSC · USDT · v1.0</div>
      </div>
    </div>
  );
}

/* ---------- Sub-components ---------- */

function FeatureCard({ icon, title, desc }) {
  return (
    <div className="landing-feature">
      <div className="landing-feature-icon">{icon}</div>
      <div className="landing-feature-body">
        <div className="landing-feature-title">{title}</div>
        <div className="landing-feature-desc">{desc}</div>
      </div>
    </div>
  );
}

function IncomeRow({ label, pct, color }) {
  return (
    <div className="landing-income">
      <div style={{ flex: 1 }}>
        <div className="landing-income-label">{label}</div>
        <div className="landing-income-bar">
          <div
            className="landing-income-fill"
            style={{ width: pct, background: color, boxShadow: `0 0 8px ${color}` }}
          />
        </div>
      </div>
      <div className="landing-income-pct" style={{ color }}>{pct}</div>
    </div>
  );
}

function StepRow({ n, title, desc }) {
  return (
    <div className="landing-step">
      <div className="landing-step-num">{n}</div>
      <div style={{ flex: 1 }}>
        <div className="landing-step-title">{title}</div>
        <div className="landing-step-desc">{desc}</div>
      </div>
    </div>
  );
}

export default Welcome;
